import { createPortal } from "react-dom";
import { useState } from "react";
import IC_BACK from "@/assets/app/back_arrow.svg?react";
import IC_CLOSE_WIN from "@/assets/common/ic_collect_close.svg?react";
import IC_VISIBLE from "@/assets/login/ic_visible.svg?react";
import IC_INVISIBLE from "@/assets/login/ic_invisible.svg?react";
import { updatePassword } from "@/services/api_user";
import useChecBeforeExit from "@/layout/checkBeforeExit";
import { message } from "antd";
import userInfoStore from "@/store/user.store";
import IcRecordFinishLoading from "@/assets/images/ic_record_finish_loading.png";
import { ErrorModel, PwdError } from "@common/utils/http/types";
import { encryptWithSha256 } from "@/utils/crypto";
import http from "@/services/http";

interface IProps {
  onClose: (success?: boolean) => void;
}

// 修改密码组件
export default function ModifyPassword({ onClose }: IProps) {
  const mobile = userInfoStore((state) => state.mobile);
  const companyId = userInfoStore((state) => state.company.companyId);
  const userId = userInfoStore((state) => state.userId);

  // 修改密码相关状态
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [clickLoading, setClickLoading] = useState(false);
  const [oldPasswordCorrect, setOldPasswordCorrect] = useState(true);
  const [newPasswordValid, setNewPasswordValid] = useState(true);
  const [confirmPasswordValid, setConfirmPasswordValid] = useState(true);

  // 从 store 获取密码冻结状态和方法
  const pwdFreezeCountDown = userInfoStore((state) => state.pwdFreezeCountDown);
  const incrementPwdError = userInfoStore((state) => state.incrementPwdError);
  const resetPwdFreeze = userInfoStore((state) => state.resetPwdFreeze);

  // 返回上一级
  const handleBack = () => {
    onClose();
  };

  // 关闭弹窗
  const handleClose = () => {
    onClose();
  };

  // 过滤密码输入：只允许英文字母、数字和常见键盘符号
  // 禁止输入空格、中文、emoji 等异常字符
  const filterPasswordInput = (value: string): string => {
    // 只保留 ASCII 可打印字符（33-126），排除空格（32）
    // 包括英文字母、数字、以及 !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~ 等符号
    return value.replace(/[^\x21-\x7E]/g, '');
  };

  const isNewPasswordValid = (value: string) => {
    // 密码验证：长度 8-16 位，且必须包含字母、数字、符号中的至少两种
    if (value) {
      const lengthValid = value.length >= 8 && value.length <= 16;
      const hasLetter = /[a-zA-Z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSymbol = /[^a-zA-Z0-9]/.test(value);
      const typeCount = [hasLetter, hasNumber, hasSymbol].filter(
        Boolean
      ).length;
      const isValid = lengthValid && typeCount >= 2;
      return isValid;
    } else {
      return true;
    }
  }

  const checkNewPasswordValid = () => {
    setNewPasswordValid(isNewPasswordValid(newPassword));
    setConfirmPasswordValid(newPassword === confirmPassword);
  }

  const isConfirmPasswordValid = (value: string) => {
    return value === newPassword;
  }

  const checkConfirmPasswordValid = () => {
    setConfirmPasswordValid(isConfirmPasswordValid(confirmPassword));
  }

  const isConfirmDisabled = () => {
    return !newPassword || !newPasswordValid || !confirmPassword || !confirmPasswordValid || pwdFreezeCountDown > 0;
  }

  // 切换密码可见性
  const togglePasswordVisible = (field: 'old' | 'new' | 'confirm') => {
    setPasswordVisible(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // 处理当前密码输入
  const onOldPasswordChange = (value: string) => {
    const filteredValue = filterPasswordInput(value);
    setOldPassword(filteredValue);
    setOldPasswordCorrect(true);
  };

  // 处理新密码输入
  const onNewPasswordChange = (value: string) => {
    const filteredValue = filterPasswordInput(value);
    setNewPassword(filteredValue);
    setNewPasswordValid(true);
  };

  // 处理确认密码输入
  const onConfirmPasswordChange = (value: string) => {
    const filteredValue = filterPasswordInput(value);
    setConfirmPassword(filteredValue);
    if (filteredValue) {
      setConfirmPasswordValid(filteredValue === newPassword);
    } else {
      setConfirmPasswordValid(true);
    }
  };

  // 提交密码修改
  const submitPasswordChange = useChecBeforeExit(async () => {
    // 验证所有字段并设置错误信息

    const oldPasswordValid = oldPassword.length > 0;
    const newPasswordValid = isNewPasswordValid(newPassword);
    const confirmPasswordValid = isConfirmPasswordValid(confirmPassword);
    setOldPasswordCorrect(oldPasswordValid);
    setNewPasswordValid(newPasswordValid);
    setConfirmPasswordValid(confirmPasswordValid);

    // 如果有任何错误，阻止提交
    if (!oldPasswordValid || !newPasswordValid || !confirmPasswordValid) {
      return;
    }

    if (oldPassword === newPassword) {
      message.error("修改密码失败，不能与上一次密码相同");
      return;
    }

    setClickLoading(true);
    const encryptedPassword = encryptWithSha256(oldPassword);
    const encryptedNewPassword = encryptWithSha256(newPassword);
    try {
      await updatePassword({
        companyId,
        userId,
        oldPassword: encryptedPassword,
        password: encryptedNewPassword
      });
      message.success("修改密码成功");
      // 重置密码冻结状态
      resetPwdFreeze();
      //清除 token
      http.clearToken();
      onClose(true);
    } catch (error) {
      const err = error as ErrorModel;
      if (err.errCode === PwdError.PwsIncorrect) {
        // 调用 store 方法增加错误次数，如果达到 3 次会自动触发冻结
        const isFreeze = incrementPwdError();
        if (isFreeze) {
          message.error("操作频繁，请稍后再试");
        } else {
          setOldPasswordCorrect(false);
          message.error(`密码修改失败，原密码错误`);
        }
      } else if (err.errCode === 'ERR_NETWORK') {
        message.error("密码修改失败，网络错误");
      } else {
        message.error("密码修改失败，" + err.errMsg);
      }
    } finally {
      setClickLoading(false);
    }
  });

  return createPortal(
    <div className="fixed top-0 w-full h-full bg-black bg-opacity-50">
      <div className="absolute flex flex-col w-105 h-106 rd-3 left-1/2 top-1/2 -translate-1/2 bg-white overflow-hidden">
        <div className="absolute flex-self-start cursor-pointer m-4.5 z-99 flex items-center gap-1" onClick={handleBack}>
          <IC_BACK className="w-3 h-3"></IC_BACK>
          <span className="text-size-3.5 text-#30343C">返回</span>
        </div>

        <IC_CLOSE_WIN
          className="absolute w-6 h-6 flex-self-end cursor-pointer m-3 z-99"
          onClick={handleClose}
        ></IC_CLOSE_WIN>

        <div className="w-full flex justify-center items-center select-none h-12 border-0 border-solid border-#ECEEF3 border-b-0.25">
          <span className="text-size-4 text-#191D26 font-500">修改密码</span>
        </div>

        <div className="flex flex-col flex-1 justify-between select-none">
          <div className="flex flex-col justify-center items-center flex pl-8 pr-11">
            <div className="w-full">
              {/* 手机号显示 - 显示完整手机号 */}
              <div className="my-6">
                <div className="bg-#D4D9E1 rd-2.5 w-full box-border h-12 flex items-center">
                  <div className="bg-#FFFFFF66 flex-1 w-full h-full text-size-4">
                    <div className="text-#191D2666 flex items-center h-full pl-4">
                      {mobile}
                    </div>
                  </div>
                </div>
              </div>

              {/* 当前密码输入框 */}
              <div className="mb-0">
                <div className="bg-#F4F6F9 rd-2.5 w-full box-border h-12 px-3 flex items-center">
                  <input
                    type={passwordVisible.old ? "text" : "password"}
                    className="flex-1 h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-4 placeholder:font-400"
                    placeholder="当前使用的密码"
                    value={oldPassword}
                    onChange={(e) => onOldPasswordChange(e.target.value)}
                  />
                  <div
                    className="ml-2 cursor-pointer text-#A1A7B2 text-size-3.5 select-none"
                    onClick={() => togglePasswordVisible("old")}
                  >
                    {passwordVisible.old ? (
                      <IC_VISIBLE className="w-4 h-4" />
                    ) : (
                      <IC_INVISIBLE className="w-4 h-4" />
                    )}
                  </div>
                </div>
                <div className="h-5 mb-1 pl-1 flex items-center">
                  {!oldPasswordCorrect && (
                    <div className="text-#FF584C text-size-3">
                      密码错误
                    </div>
                  )}
                </div>
              </div>

              {/* 新密码输入框 */}
              <div className="mb-0">
                <div className="bg-#F4F6F9 rd-2.5 w-full box-border h-12 px-3 flex items-center">
                  <input
                    type={passwordVisible.new ? "text" : "password"}
                    className="flex-1 h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-4 placeholder:font-400"
                    placeholder="新密码"
                    value={newPassword}
                    maxLength={16}
                    onBlur={() => checkNewPasswordValid()}
                    onChange={(e) => onNewPasswordChange(e.target.value)}
                  />
                  <div
                    className="ml-2 cursor-pointer text-#A1A7B2 text-size-3.5 select-none"
                    onClick={() => togglePasswordVisible("new")}
                  >
                    {passwordVisible.new ? (
                      <IC_VISIBLE className="w-4 h-4" />
                    ) : (
                      <IC_INVISIBLE className="w-4 h-4" />
                    )}
                  </div>
                </div>
                <div className="h-5 mb-1 pl-1 flex items-center">
                  {!newPasswordValid && (
                    <div className="text-#FF584C text-size-3">
                      输入密码需8-16位字母/数字/符号至少包含2种
                    </div>
                  )}
                </div>
              </div>

              {/* 确认新密码输入框 */}
              <div className="mb-0">
                <div className="bg-#F4F6F9 rd-2.5 w-full box-border h-12 px-3 flex items-center">
                  <input
                    type={passwordVisible.confirm ? "text" : "password"}
                    className="flex-1 h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-4 placeholder:font-400"
                    placeholder="再次输入新密码"
                    value={confirmPassword}
                    maxLength={16}
                    onBlur={() => checkConfirmPasswordValid()}
                    onChange={(e) => onConfirmPasswordChange(e.target.value)}
                  />
                  <div
                    className="ml-2 cursor-pointer text-#A1A7B2 text-size-3.5 select-none"
                    onClick={() => togglePasswordVisible("confirm")}
                  >
                    {passwordVisible.confirm ? (
                      <IC_VISIBLE className="w-4 h-4" />
                    ) : (
                      <IC_INVISIBLE className="w-4 h-4" />
                    )}
                  </div>
                </div>
                <div className="h-5 mb-1 pl-1 flex items-center">
                  {!confirmPasswordValid && (
                    <div className="text-#FF584C text-size-3">
                      两次密码输入不一致
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full h-14 b-t-1 b-t-solid b-t-#ECEEF3 flex px-4 items-center justify-end gap-4">
            <button
              className="w-22 h-8 rd-1.5 text-#626773 text-size-3.5 bg-transparent cursor-pointer hover:opacity-90 border-1 border-solid border-#ECEEF3"
              onClick={handleBack}
            >
              取消
            </button>
            <button
              disabled={isConfirmDisabled() || clickLoading}
              className={`w-22 h-8 rd-1.5 text-white text-size-3.5 outline-none border-none cursor-pointer bg-#4981F2 ${isConfirmDisabled() ? "opacity-30 cursor-not-allowed" : ""
                }`}
              onClick={submitPasswordChange}
            >
              {clickLoading ? (
                <img src={IcRecordFinishLoading} className="rotation size-4" />
              ) : pwdFreezeCountDown > 0 ? (
                `${pwdFreezeCountDown}s`
              ) : (
                "确定"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
} 